import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../services/supabase';

const AuthContext = createContext();

const TOKEN_KEY = 'cafegoo_admin_token';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    verifyStoredToken();
  }, []);

  // Saxlanmış tokeni server-də yoxla
  const verifyStoredToken = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;

      const { data, error } = await supabase.rpc('admin_verify_session', {
        p_token: token,
      });

      if (error || !data?.valid) {
        localStorage.removeItem(TOKEN_KEY);
        return;
      }

      setAdmin(data.admin);
    } catch (_) {
      localStorage.removeItem(TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.rpc('admin_login', {
        p_email:    email,
        p_password: password,
      });

      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Giriş uğursuz oldu');

      if (data.needs_totp) {
        return { success: true, needsTotp: true, pendingToken: data.pending_token };
      }

      // Yalnız token saxlanır — admin data localStorage-da deyil
      localStorage.setItem(TOKEN_KEY, data.token);
      setAdmin(data.admin);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const verifyTotp = async (pendingToken, code) => {
    try {
      const { data, error } = await supabase.functions.invoke('admin-2fa', {
        body: { action: 'verify', pending_token: pendingToken, code },
      });

      if (error) throw new Error(error.message);
      if (data?.error) {
        const messages = {
          INVALID_CODE: 'Kod səhvdir',
          EXPIRED_OR_INVALID: 'Sessiya vaxtı bitib, yenidən giriş edin',
        };
        throw new Error(messages[data.error] || data.error);
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      setAdmin(data.admin);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        await supabase.rpc('admin_logout', { p_token: token });
      }
    } catch (_) {}
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(null);
  };

  const getAdminToken = () => localStorage.getItem(TOKEN_KEY);

  return (
    <AuthContext.Provider value={{ admin, loading, login, verifyTotp, logout, getAdminToken }}>
      {children}
    </AuthContext.Provider>
  );
};
